import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

/*******INTERNAL IMPORTS********/
import { DisplaySpinnerContext } from "../../context/ContextIndex";
import {
  DataGridTable,
  GridRowAction,
  ConfirmationPopup,
  notifyToast,
  ToastType,
  FormPage,
} from "../../components-old/ComponentsIndex";
import { PricingPortalAPI } from "../../apis/api-index";
import { Box } from "@mui/material";
import { MdDelete } from "react-icons/md";
import { handlePostAction } from "../../utils/event-api-invoke";
import { useSelector } from "react-redux";
import { selectSessionConfig } from "../../store/store-selector";

const QuoteApprovalReassignmentListForm = () => {
  /**************************************************
  //Form handlers & hooks
  **************************************************/
  const { t } = useTranslation();
  const navigate = useNavigate();
  const spinnerCtx = useContext(DisplaySpinnerContext);
  const settingsConfig = useSelector(selectSessionConfig);

  /**************************************************
  //Form state variables
  **************************************************/
  const [quoteApprovalReassignmentRecordList, setQuoteApprovalReassignmentRecordList] = useState([]);
  const [dataGridPageSize, setDataGridPageSize] = useState(10);
  const [totalQuoteApprovalReassignment, setTotalQuoteApprovalReassignment] = useState(0);
  const [gridPageSizeOptions /*setGridPageSizeOptions*/] = useState([10, 20, 50]);
  const [popupOpen, setPopupOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  /**************************************************
  //Event definitions
  **************************************************/

  //on click of add icon for adding leave
  const handleCreateQuoteApprovalReassignmentClick = () => {
    navigate("/quoteapprovalreassignment");
  };

  //onclick of confirm of delete popup
  const handleConfirm = async () => {
    await deleteQuoteApprovalReassignment(selectedRow);
    setPopupOpen(false);
    setSelectedRow(null);
  };

  //on click of cancel of delete popup
  const handleCancel = () => {
    setSelectedRow(null);
    setPopupOpen(false);
  };

  //onClick of delete in lits
  const showDeleteQuoteApprovalReassignmentPopup = (event) => {
    try {
      setSelectedRow(event);
      setPopupOpen(true);
    } catch (err) {
      alert(err);
    }
  };

  //on page change
  const handlePageSizeChange = (newPageSize) => {
    setDataGridPageSize(newPageSize);
  };

  /**************************************************
  //Fetch API & utility
  **************************************************/

  //Delete API call
  const deleteQuoteApprovalReassignment = async (param) => {
    try {
      spinnerCtx.setLoadingIndicator(true);
      let _postRequest = {
        countryCode: settingsConfig.countryCode,
        businessCode: settingsConfig.businessCode,
        approvalReassignment: [
          {
            ConfigId: param.id,
            Deleted: true,
          },
        ]
      };
      let res = await handlePostAction(_postRequest, "approvalcoveringmanagerconfig");
      if (res && res.status && res.status === 200) {
        notifyToast("Leave plan deleted successfully", ToastType.SUCCESS);
        await postActionRefreshList(param.id);
      } else {
        notifyToast("Failed", ToastType.ERROR);
        spinnerCtx.setLoadingIndicator(false);
      }
    } catch (err) {
      alert(err);
    }
  };

  //Fetch API for list
  const postActionRefreshList = async (SectorId) => {
    try {
      let _quoteApprovalReassignmentRecord = await PricingPortalAPI.getList(
        settingsConfig.businessCode,
        settingsConfig.countryCode,
        "getQuoteApprovalReassignmentList",
        "quoteApprovalReassignment"
      );
      updateQuoteApprovalReassignmentList(SectorId, _quoteApprovalReassignmentRecord);
    } catch (err) {
      alert(err);
    }
  };

  //update list after delete
  const updateQuoteApprovalReassignmentList = (SectorId, record) => {
    try {
      let _updatedQuoteApprovalReassignmentList = [...quoteApprovalReassignmentRecordList];
      let _index = _updatedQuoteApprovalReassignmentList.findIndex((item) => item.id === SectorId);

      _updatedQuoteApprovalReassignmentList.splice(_index, 1);

      const newTotalQuoteApprovalReassignment = totalQuoteApprovalReassignment - 1;
      const newPageSize = Math.min(newTotalQuoteApprovalReassignment, dataGridPageSize);
      setTotalQuoteApprovalReassignment(newTotalQuoteApprovalReassignment);
      setDataGridPageSize(newPageSize);
      setQuoteApprovalReassignmentRecordList(_updatedQuoteApprovalReassignmentList);
      spinnerCtx.setLoadingIndicator(false);
    } catch (err) {
      alert(err);
    }
  };
  /**************************************************
  //JSX delcaration
  **************************************************/

  //data grid schema
  const dataGridSchema = [
    {
      field: "leaveStartDate",
      headerName: t("quoteApprovalReassignmentList.leaveStartDate"),
      flex: 2
    },
    {
      field: "leaveEndDate",
      headerName: t("quoteApprovalReassignmentList.leaveEndDate"),
      flex: 2
    },
    {
      field: "coveringManager",
      headerName: t("quoteApprovalReassignmentList.coveringManager"),
      flex: 2
    },
    {
      field: "recordResponse",
      hide: true,
    },
    {
      field: "action",
      headerName: t,
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        return (
          <>
            <Box width={25} />
            {
              <>
                <GridRowAction width={25} height={25} handleSubmit={() => showDeleteQuoteApprovalReassignmentPopup(params.row)}>
                  <MdDelete fontSize="medium" style={{ color: "#A62B23" }} />
                </GridRowAction>
              </>
            }
          </>
        );
      },
    },
  ];

  //button configs for add and download list
  const buttonConfigs = [
    {
      buttonText: t("quoteApprovalReassignmentList.addLeave"),
      buttonType: "filled",
      onClick: handleCreateQuoteApprovalReassignmentClick,
      buttonColor: "#A62B23",
      textColor: "#fff",
    }
  ];

  /**************************************************
  //Form on load useEffect
  **************************************************/
  useEffect(() => {
    const setQuoteApprovalReassignmentList = (quoteApprovalReassignmentList) => {
      try {
        quoteApprovalReassignmentList.sort((a, b) => new Date(b.ModifiedDate).getTime() - new Date(a.ModifiedDate).getTime());
        let _quoteApprovalReassignmentList = quoteApprovalReassignmentList.map((item) => {
          return {
            id: item.ConfigId,
            leaveStartDate: (new Date((item.LeaveStartDate).split("T")[0]).toLocaleString("en-GB").split(","))[0],
            leaveEndDate: (new Date((item.LeaveEndDate).split("T")[0]).toLocaleString("en-GB").split(","))[0],
            coveringManager: item.CoveringManager,
            recordResponse: item,
          };
        });

        setQuoteApprovalReassignmentRecordList(_quoteApprovalReassignmentList);
        setTotalQuoteApprovalReassignment(_quoteApprovalReassignmentList.length);
        spinnerCtx.setLoadingIndicator(false);
      } catch (err) {
        spinnerCtx.setLoadingIndicator(false);
        alert(err);
      }
    };

    const FetchQuoteApprovalReassignmentList = async () => {
      try {
        let _lists = await PricingPortalAPI.getList(
          settingsConfig.businessCode,
          settingsConfig.countryCode,
          "getQuoteApprovalReassignmentList",
          "approvalcoveringmanagerconfig"
        );
        setQuoteApprovalReassignmentList(_lists);
      } catch (err) {
        alert(err);
      }
    };
    spinnerCtx.setLoadingIndicator(true);
    FetchQuoteApprovalReassignmentList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <FormPage isList={true}>
      <DataGridTable
        rows={quoteApprovalReassignmentRecordList}
        columns={dataGridSchema}
        pageSize={dataGridPageSize}
        pageSizeOptions={gridPageSizeOptions}
        onPageSizeChange={handlePageSizeChange}
        totalRowCount={totalQuoteApprovalReassignment}
        toolbarHeader={t("quoteApprovalReassignmentList.header")}
        toolbarFilterPlaceholder={t("quoteApproval.toolbarFilterPlaceholder")}
        buttonConfigs={buttonConfigs}
      />
      <ConfirmationPopup
        isOpen={popupOpen}
        onClose={handleCancel}
        onConfirm={handleConfirm}
        confirmationText={t("popup.deleteQuoteApprovalReassignmentConfirmationMessage")}
        eventObj={selectedRow}
      />
    </FormPage>
  );
};

export default QuoteApprovalReassignmentListForm;
